<template>
  <div ref="autoCompleteEl" class="input-container" :class="{'company-input': regFlowLogin}">
    <input
      v-bind="field"
      class="autosuggest-input"
      :id="id"
      type="text"
      v-model="inputValue"
      @input="onInput"
      @focus="onFocus"
      @blur="onBlur"
      :placeholder="placeHolderValue"
      :name="name"
      :class="inputProps.class"
      :disabled="inputProps.disabled"
    />
    <span class="input-placeholder" :class="{'animate' : inputValue || !animatePlaceholderToLabel}">{{label}}</span>
    <ul v-if="showSuggestions && filteredSuggestions.length" class="suggestions-list">
      <li
        v-for="(suggestion, index) in filteredSuggestions"
        :key="index"
        @mousedown.prevent="selectSuggestion(suggestion)"
        class="suggestion-item"
      >
        {{ suggestion.Name }}
      </li>
    </ul>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue';

// Define component name
defineOptions({
  name: 'AutoComplete'
});

// Props
const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  suggestions: {
    type: Array,
    required: true,
  },
  limit: {
    type: Number,
    default: 10,
  },
  inputProps: {
    type: Object,
    default: () => ({}),
  },
  animatePlaceholderToLabel: {
    type: Boolean,
    default: true,
  },
  placeHolderValue: {
    type: String,
    default: '',
  },
  name: {
    type: String,
    required: true,
  },
  label: {
    type: String,
    required: true,
  },
  regFlowLogin: {
    type: Boolean,
    default: false,
  },
  modelValue: {
    type: String,
    default: '',
  },
  field: Object,
});

// Emits
const emit = defineEmits(['update:modelValue', 'blur', 'onSelected']);


// Reactive data
const inputValue = ref(props.modelValue);
const showSuggestions = ref(false);
const autoCompleteEl = ref(null);

// Computed properties
const filteredSuggestions = computed(() => {
  return props.suggestions
    .filter((suggestion) =>
      suggestion.Name.toLowerCase().includes(inputValue.value.toLowerCase())
    )
    .slice(0, props.limit);
});

// Watchers
watch(() => props.modelValue, (newValue) => {
  inputValue.value = newValue;
});


// Methods
const onInput = () => {
  showSuggestions.value = true;
  emit('update:modelValue', inputValue.value);
};

const onBlur = () => {
  // Add a small delay before hiding suggestions to allow time for click events
  setTimeout(() => {
    showSuggestions.value = false;
    emit('blur', inputValue.value);
  }, 150);
};


const selectSuggestion = (suggestion) => {
  // Update local input value
  inputValue.value = suggestion.Name;

  // Hide suggestions immediately when explicitly selected
  showSuggestions.value = false;

  // Emit the selected value with the full suggestion object
  emit('onSelected', { item: suggestion });

  // Emit update:modelValue for v-model binding (primary event)
  emit('update:modelValue', suggestion.Name);

  // Sometimes Vue's reactivity needs help with timing for input updates
  // This ensures the input shows the correct value
  nextTick(() => {
    if (autoCompleteEl.value && autoCompleteEl.value.querySelector('input')) {
      autoCompleteEl.value.querySelector('input').value = suggestion.Name;
    }
  });
};
</script>

<style lang="stylus" scoped>
.autosuggest-input {
  width: 100%;
  height: 50px;
  border: none;
  background: none;
  color: #868894;
  border-bottom: 1px solid #DEE2E6;
  box-sizing: border-box;
  font-size: 1.25rem;
  font-family: sans-pro;
  padding-left: 10px;

  &:focus {
    outline: none;
  }

  &:disabled {
    cursor: no-drop;
  }

  &:invalid {
    border-color: red;
  }

  &:-webkit-autofill {
    -webkit-box-shadow: 0 0 0 30px white inset !important;
  }
}
.suggestions-list {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #ccc;
  z-index: 1000;
  max-height: 200px;
  overflow-y: auto;
  list-style: none;
  padding: 0;
  margin: 0;
}

.suggestion-item {
  padding: 10px;
  cursor: pointer;
}

.suggestion-item:hover {
  background-color: #f0f0f0;
}

.company-input {
  border: 1px solid #D3D3D3;
  border-radius: 4px;
   &:focus-within {
     border: 1px solid #2E90FA;
     border-radius: 4px;
   }
  .autosuggest-input {
    font-family: 'Lato',sans-serif;
    border-bottom: none;
    font-size: 14px;
    color: #3a4145;
  }
  input {
    height: 40px;
    margin-top: 0;
    padding-top: 0;
  }
  .input-placeholder {
    font-size: 14px;
    font-family: 'Lato',sans-serif;
    max-width: calc(100% - 50px) !important;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    padding: 12px 0 12px 10px !important;
  }
  .animate, input:focus + span {
    font-size:11px;
    background-color: #fff;
    width: auto;
    padding: 0px 2px 2px 2px !important;
    transform: translateY(-6.5px) translateX(10px);
  }
  input:focus + span{
    color: #2E90FA;
  }
  .outer-tooltip-container{
    position: absolute;
    right: 0;
    top: 0;
    background-color: #F7F7F7;
    width: 32px;
    height: 40px;
    text-align: center;
  }
}
</style>